import {ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MaterialModule} from "../../../../material.module";

import {environment} from "../../../../../environments/environment";
import {Subscription} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatChipInputEvent} from '@angular/material/chips';
import {DisabledButtonReasonComponent} from "../../../shared/disabled-button-reason/disabled-button-reason.component";
import {ColorFieldComponent} from '../../../../shared/color-field/color-field.component';
import {WIDGET_STYLING_FIELDS} from '../../../../models/widget-styling.model';

interface WidgetColorControl {
  key: keyof IWidgetColorForm;
  label: string;
  hint?: string;
  supportsTransparent?: boolean;
}

interface WidgetColorGroup {
  title: string;
  description?: string;
  controls: WidgetColorControl[];
}

interface IWidgetColorForm {
  backgroundColor: string;
  textColor: string;
  closeButtonBackground: string;
  closeButtonColor: string;
  closeButtonShadow: string;
  popupShadow: string;
  triangleShadow: string;
  launcherBackground: string;
  launcherShadow: string;
}

@Component({
  selector: 'app-widget-settings',
  templateUrl: './widget-settings.component.html',
  styleUrls: ['./widget-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MaterialModule, ReactiveFormsModule, DisabledButtonReasonComponent, ColorFieldComponent]
})
export class WidgetSettingsComponent implements OnInit, OnDestroy {
    private renderer = inject(Renderer2);
    private snackBar = inject(MatSnackBar);


  @Input() widgetForm: FormGroup;
  @Input() settingsForm: FormGroup;
  @Input({required: true}) zIndexControl!: FormControl<number | null>;
  @Input() chatbotId: string;
  @Input() clientId: string;
  @Input() cookiesToCollect: string[] = [];
  readonly widgetLoaderCopyMessage = 'Widget loader script copied to clipboard';
  readonly inlineLauncherCopyMessage = 'Inline launcher snippet copied to clipboard';
  readonly inlineChatCopyMessage = 'Inline chat snippet copied to clipboard';
  readonly widgetCookieCopyMessage = 'Cookie configuration copied to clipboard';
  readonly widgetDynamicContextCopyMessage = 'Dynamic widget context snippet copied to clipboard';
  readonly launcherShapeOptions = [
    { key: 'circle', value: 'Circle' },
    { key: 'rectangle', value: 'Rectangle' }
  ];
  readonly stylingFields = WIDGET_STYLING_FIELDS;

  aiLogo1Preview: string | null = null;
  aiLogo2Preview: string | null = null;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  private logoSubscriptions: Subscription[] = [];

  colorGroups: WidgetColorGroup[] = [
    {
      title: 'Pop-up Styling',
      description: 'Applies to the widget bubble, text, and close button.',
      controls: [
        { key: 'backgroundColor', label: 'Widget Background', hint: 'Background color of the widget bubble.' },
        { key: 'textColor', label: 'Widget Text', hint: 'Default text color inside the widget bubble.' },
        { key: 'launcherBackground', label: 'Launcher Background', hint: 'Use transparent to keep the launcher rectangle clear unless a brand color is explicitly required.', supportsTransparent: true },
        { key: 'closeButtonBackground', label: 'Close Button Background', hint: 'Background for the close button.' },
        { key: 'closeButtonColor', label: 'Close Button Icon', hint: 'Icon color for the close button.' }
      ]
    }
  ];

  readonly defaultPickerColors: Record<keyof IWidgetColorForm, string> = {
    backgroundColor: '#FFFFFF',
    textColor: '#384258',
    closeButtonBackground: '#121413',
    closeButtonColor: '#FFFFFF',
    closeButtonShadow: '#121413',
    popupShadow: '#121413',
    triangleShadow: '#121413',
    launcherBackground: '#E31100',
    launcherShadow: '#121413'
  };

  get widgetLoaderSnippet(): string {
    const client = this.clientId ?? '';
    const chatbot = this.chatbotId ?? '';
    return `<script>\n    (function(){\n      window.shineSettings={client:'${client}',chatbot:'${chatbot}'};\n      var s=document.createElement('script');s.type='text/javascript';\n      s.src='${environment.widgetScriptUrl}';\n      s.async=true;document.head.appendChild(s);\n    })();\n</script>`;
  }

  get inlineLauncherSnippet(): string {
    const client = this.clientId ?? '';
    const chatbot = this.chatbotId ?? '';
    return `<shine-chat-launcher aria-label="Open chat"></shine-chat-launcher>\n<script>\n    (function(){\n      window.shineSettings={client:'${client}',chatbot:'${chatbot}',inlineOnly:true};\n      var s=document.createElement('script');s.type='text/javascript';\n      s.src='${environment.widgetScriptUrl}';\n      s.async=true;document.head.appendChild(s);\n    })();\n</script>`;
  }

  get inlineChatSnippet(): string {
    const client = this.clientId ?? '';
    const chatbot = this.chatbotId ?? '';
    return `<shine-inline-chat client="${client}" chatbot="${chatbot}"></shine-inline-chat>\n<script>\n    (function(){\n      window.shineSettings={client:'${client}',chatbot:'${chatbot}',inlineOnly:true};\n      var s=document.createElement('script');s.type='text/javascript';\n      s.src='${environment.widgetScriptUrl}';\n      s.async=true;document.head.appendChild(s);\n    })();\n</script>`;
  }

  get widgetCookieSnippet(): string {
    const client = this.clientId ?? '';
    const chatbot = this.chatbotId ?? '';
    const contextKeys = this.contextKeys.length > 0 ? this.contextKeys : ['hubspotutk', 'user_vehicles'];
    const serializedKeys = contextKeys.map(key => `'${key.replace(/'/g, "\\'")}'`).join(', ');
    return `<script>\n    window.shineSettings = window.shineSettings || { client: '${client}', chatbot: '${chatbot}' };\n    window.shineSettings.cookiesToCollect = [${serializedKeys}];\n</script>`;
  }

  get widgetDynamicContextSnippet(): string {
    const client = this.clientId ?? '';
    const chatbot = this.chatbotId ?? '';
    return `<script>\n    window.shineSettings = window.shineSettings || { client: '${client}', chatbot: '${chatbot}' };\n    window.shineSettings.dynamicWidgetContext = {\n      audience: 'B2B buyers',\n      campaign: 'Q2 laptop refresh',\n      priority: 'Guide visitors to the managed onboarding offer'\n    };\n</script>`;
  }

  ngOnInit(): void {
    this.initializeLogoPreview('aiLogo1');
    this.initializeLogoPreview('aiLogo2');
  }

  ngOnDestroy(): void {
    this.logoSubscriptions.forEach(sub => sub.unsubscribe());
  }

  get colorsGroup(): FormGroup {
    return this.widgetForm.get('colors') as FormGroup;
  }

  get contextKeys(): string[] {
    const formKeys = this.settingsForm?.get('cookiesToCollect')?.value;
    const source = Array.isArray(formKeys) ? formKeys : this.cookiesToCollect;
    return Array.from(new Set(
      (source ?? [])
        .map((key: string) => (key || '').trim())
        .filter((key: string) => key.length > 0)
    ));
  }

  get dynamicWidgetTextControl(): FormControl<boolean> {
    return this.settingsForm.get('dynamicWidgetText') as FormControl<boolean>;
  }

  get dynamicWidgetTextInstructionControl(): FormControl<string> {
    return this.settingsForm.get('dynamicWidgetTextInstruction') as FormControl<string>;
  }

  get testWidgetDisabled(): boolean {
    return this.testWidgetDisabledReason.length > 0;
  }

  get testWidgetDisabledReason(): string {
    if (this.tested) return 'Refresh the page before testing the widget again.';
    return '';
  }

  getColorControl(key: keyof IWidgetColorForm): FormControl<string | null> {
    return this.colorsGroup.get(key as string) as FormControl<string | null>;
  }

  injectScript(): void {
    // Define settings on the window

    this.tested = true;

    // @ts-ignore
    (window as any)['shineSettings'] = { client: this.clientId, chatbot: this.chatbotId, cookiesToCollect: this.contextKeys };

    // Create and append the script element
    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = environment.widgetScriptUrl;
    script.async = true;
    this.renderer.appendChild(document.head, script);
  }

  protected readonly environment = environment;
  tested: boolean;

  get imagesGroup(): FormGroup {
    return this.widgetForm.get('images') as FormGroup;
  }

  get isLogoSwapEnabled(): boolean {
    return !!this.imagesGroup?.get('logoSwapEnabled')?.value;
  }

  copySnippet(snippet: string, event: Event, successMessage?: string): void {
    const button = event.currentTarget as HTMLElement | null;
    const showFeedbackIcon = () => {
      const icon = button?.querySelector('mat-icon') as HTMLElement | null;
      if (!icon) {
        return;
      }
      const previous = icon.innerText;
      icon.innerText = 'check';
      setTimeout(() => icon.innerText = previous, 1500);
    };

    const notifyCopied = () => {
      showFeedbackIcon();
      const trimmedMessage = successMessage?.trim();
      const message = trimmedMessage && trimmedMessage.length > 0 ? trimmedMessage : 'Copied to clipboard';
      this.snackBar.open(message, undefined, { duration: 2500 });
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(snippet)
        .then(notifyCopied)
        .catch(() => {
          this.copyFallback(snippet);
          notifyCopied();
        });
    } else {
      this.copyFallback(snippet);
      notifyCopied();
    }
  }

  private copyFallback(snippet: string): void {
    const textarea = this.renderer.createElement('textarea');
    textarea.value = snippet;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    this.renderer.appendChild(document.body, textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } finally {
      this.renderer.removeChild(document.body, textarea);
    }
  }

  onLogoSelected(key: 'aiLogo1' | 'aiLogo2', event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    if (!this.isSupportedImageFile(file)) {
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (dataUrl) {
        this.imagesGroup.get(key)?.setValue(dataUrl);
        this.imagesGroup.get(key)?.markAsDirty();
        this.imagesGroup.get(key)?.markAsTouched();
        this.updateLogoPreview(key, dataUrl);
      }
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  private initializeLogoPreview(key: 'aiLogo1' | 'aiLogo2'): void {
    const control = this.imagesGroup.get(key);
    if (!control) {
      return;
    }

    this.updateLogoPreview(key, control.value);
    const subscription = control.valueChanges.subscribe(value => this.updateLogoPreview(key, value));
    this.logoSubscriptions.push(subscription);
  }

  private updateLogoPreview(key: 'aiLogo1' | 'aiLogo2', value: any): void {
    const trimmed = (value ?? '').toString().trim();
    const preview = trimmed.length > 0 ? trimmed : null;
    if (key === 'aiLogo1') {
      this.aiLogo1Preview = preview;
    } else {
      this.aiLogo2Preview = preview;
    }
  }

  private isSupportedImageFile(file: File): boolean {
    return file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.svg');
  }

  addContextKey(event: MatChipInputEvent): void {
    const input = event.value ?? '';
    const pieces = input
      .split(',')
      .map(s => s.trim())
      .filter(s => !!s);

    if (!pieces.length) {
      event.chipInput?.clear();
      return;
    }

    const control = this.settingsForm.get('cookiesToCollect');
    const current: string[] = control?.value || [];
    const cleaned = Array.from(new Set([...current, ...pieces].map(s => s.trim()).filter(s => s.length)));
    control?.setValue(cleaned);
    control?.markAsDirty();
    event.chipInput?.clear();
  }

  removeContextKey(contextKey: string): void {
    const control = this.settingsForm.get('cookiesToCollect');
    const current: string[] = control?.value || [];
    control?.setValue(current.filter(c => c !== contextKey));
    control?.markAsDirty();
  }
}
